import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { motion } from "framer-motion";
import { format, addDays, startOfDay, parseISO, isBefore, isToday } from "date-fns";
import { Calendar, Filter, ChevronLeft, ChevronRight, Loader2, Search, User } from "lucide-react";
import axios from "axios";
import { ClassCard } from "../components/ClassCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SchedulePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [schedules, setSchedules] = useState([]);
  const [styles, setStyles] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  // weekStart drives the API fetch window; selectedDate filters the displayed day
  const [weekStart, setWeekStart] = useState(startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [selectedStyle, setSelectedStyle] = useState("all");
  const [selectedInstructor, setSelectedInstructor] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSchedules, setTotalSchedules] = useState(0);
  const ITEMS_PER_PAGE = 12;

  useScrollToTopWhenLoading(loading);

  // Check for query params from WordPress
  const scheduleId = searchParams.get("schedule_id");

  // Handle WordPress CTA redirects — runs once on mount
  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "studio") { navigate("/studio-booking"); return; }
    if (type === "events") { navigate("/events"); return; }
    if (type === "packages") { navigate("/packages"); return; }
    if (type === "private") { navigate("/private-sessions"); return; }
    if (type === "class") {
      const className = searchParams.get("class_name");
      if (className) setSearchQuery(decodeURIComponent(className));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // If redirected from WordPress with specific schedule, go to booking
    if (scheduleId) {
      navigate(`/book?schedule_id=${scheduleId}`);
      return;
    }

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, selectedStyle, selectedInstructor, scheduleId, navigate, currentPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch styles and instructors
      const [stylesRes, instructorsRes] = await Promise.all([
        axios.get(`${API}/styles`),
        axios.get(`${API}/instructors`),
      ]);
      setStyles(stylesRes.data || []);
      setInstructors(instructorsRes.data || []);

      // Fetch schedules for the full week window
      const params = {
        from_date: weekStart.toISOString(),
        to_date: addDays(weekStart, 7).toISOString(),
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      const schedulesRes = await axios.get(`${API}/schedules`, { params });
      setSchedules(schedulesRes.data.schedules || []);
      setTotalPages(schedulesRes.data.total_pages || 1);
      setTotalSchedules(schedulesRes.data.total || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBook = (schedule) => {
    navigate(`/book?schedule_id=${schedule.id}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePrevWeek = () => {
    const newStart = addDays(weekStart, -7);
    setWeekStart(newStart);
    setSelectedDate(newStart);
    setCurrentPage(1);
  };

  const handleNextWeek = () => {
    const newStart = addDays(weekStart, 7);
    setWeekStart(newStart);
    setSelectedDate(newStart);
    setCurrentPage(1);
  };

  const isScheduleExpired = (schedule) => {
    const startTime = parseISO(schedule.start_datetime);
    return isBefore(startTime, new Date());
  };

  const dateRange = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");

  // Filter schedules to the selected day only
  const filteredSchedules = schedules.filter((schedule) => {
    // Filter out past classes
    if (isScheduleExpired(schedule)) return false;

    // Filter out inactive classes
    if (schedule.dance_class?.is_active === false) return false;

    // Only show selected day
    if (!schedule.start_datetime?.startsWith(selectedDateStr)) return false;

    // Filter by style
    if (selectedStyle !== "all" && schedule.dance_class?.dance_style_id !== selectedStyle) {
      return false;
    }

    // Filter by instructor
    if (selectedInstructor !== "all" && schedule.instructor_id !== selectedInstructor) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const className = schedule.dance_class?.name?.toLowerCase() || "";
      const instructorName = schedule.instructor?.name?.toLowerCase() || "";
      if (!className.includes(query) && !instructorName.includes(query)) {
        return false;
      }
    }

    return true;
  });

  // Count-aware grid class for desktop
  const gridClass =
    filteredSchedules.length === 1
      ? "flex justify-center"
      : filteredSchedules.length === 2
      ? "flex flex-wrap justify-center gap-6"
      : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6";

  const cardWrapperClass =
    filteredSchedules.length === 1
      ? "w-full max-w-md"
      : filteredSchedules.length <= 2
      ? "w-full md:max-w-sm"
      : "w-full";

  return (
    <div className="min-h-screen pb-4 overflow-x-hidden" data-testid="schedule-page">
      <SEOHead page="home" />
      <div className="container-empire">
        {/* Hero Section */}
        <section className="relative mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="bg-empire-gold/20 text-empire-gold mb-4 hover:text-white">
              Empire Dance Studios
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Class Schedule
            </h1>
            <p className="text-neutral-400 text-lg max-w-xl mx-auto">
              Find your perfect class and book your spot today
            </p>
          </motion.div>
        </section>

        {/* Filters Section */}
        <section className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Search by class name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-empire pl-10"
                data-testid="search-classes"
              />
            </div>

            <div className="grid grid-cols-2 lg:contents gap-4">
              {/* Style Filter */}
              <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                <SelectTrigger className="w-full lg:w-[200px] bg-neutral-900 border-neutral-800" data-testid="style-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Styles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Styles</SelectItem>
                  {styles.map((style) => (
                    <SelectItem key={style.id} value={style.id}>
                      {style.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Choreographer Filter */}
              <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
                <SelectTrigger className="w-full lg:w-[200px] bg-neutral-900 border-neutral-800" data-testid="instructor-filter">
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Choreographers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Choreographers</SelectItem>
                  {instructors.map((instructor) => (
                    <SelectItem key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Date Navigation */}
        <section className="mb-8">
          <div className="flex items-center justify-between gap-2 mb-4 overflow-x-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevWeek}
              className="border-neutral-700 hover:bg-neutral-800 w-auto"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous Week
            </Button>

            <span className="text-sm md:text-base text-white font-medium text-center whitespace-nowrap">
              {format(weekStart, "MMMM d")} - {format(addDays(weekStart, 6), "MMMM d, yyyy")}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={handleNextWeek}
              className="border-neutral-700 hover:bg-neutral-800 w-auto"
            >
              Next Week
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Day Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {dateRange.map((date) => {
              const isSelected = format(date, "yyyy-MM-dd") === selectedDateStr;
              const daySchedules = schedules.filter(
                (s) => s.start_datetime?.startsWith(format(date, "yyyy-MM-dd")) && !isScheduleExpired(s)
              );

              return (
                <Button
                  key={date.toISOString()}
                  variant={isSelected ? "default" : "outline"}
                  onClick={() => setSelectedDate(startOfDay(date))}
                  className={`flex-shrink-0 flex-col h-auto py-2 px-4 ${isSelected
                      ? "bg-empire-gold text-black hover:bg-empire-gold/90"
                      : "border-neutral-700 hover:bg-neutral-800"
                    }`}
                >
                  <span className="text-xs opacity-70">{format(date, "EEE")}</span>
                  <span className="text-lg font-bold">{format(date, "d")}</span>
                  {daySchedules.length > 0 && (
                    <Badge className={`text-xs mt-1 ${isSelected ? "bg-black/20" : "bg-empire-gold/20 text-empire-gold"}`}>
                      {daySchedules.length}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
        </section>

        {/* Schedule Grid */}
        <section>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : filteredSchedules.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Calendar className="w-16 h-16 text-neutral-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No upcoming classes</h3>
              <p className="text-neutral-500">Check back later or try a different date.</p>
            </motion.div>
          ) : (
            <div className="space-y-8">
              <div>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-empire-gold" />
                  {format(selectedDate, "EEEE, MMMM d")}
                  {isToday(selectedDate) && (
                    <Badge className="bg-empire-gold/20 text-empire-gold">Today</Badge>
                  )}
                  <Badge variant="secondary" className="ml-2">
                    {filteredSchedules.length} {filteredSchedules.length === 1 ? "class" : "classes"}
                  </Badge>
                </h2>
                <div className={gridClass}>
                  {filteredSchedules
                    .sort((a, b) => a.start_datetime?.localeCompare(b.start_datetime))
                    .map((schedule) => {
                      const expired = isScheduleExpired(schedule);
                      return (
                        <div key={schedule.id} className={cardWrapperClass}>
                          <ClassCard
                            schedule={schedule}
                            onBook={handleBook}
                            expired={expired}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-neutral-800">
                  <p className="text-sm text-neutral-400">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalSchedules)} of {totalSchedules} classes
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-neutral-700 hover:bg-neutral-800"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Prev
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className={currentPage === pageNum
                              ? "bg-empire-gold text-black hover:bg-empire-gold/90"
                              : "border-neutral-700 hover:bg-neutral-800"
                            }
                            data-testid={`page-${pageNum}-btn`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-neutral-700 hover:bg-neutral-800"
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Wellness Partner Banner */}
        <section className="mt-10 mb-4">
          <div className="glass-gold rounded-2xl p-4 md:p-6 flex flex-row items-center gap-4 md:gap-6">
            {/* Partner logo */}
            <div className="flex-shrink-0 bg-white rounded-lg p-2 md:p-3">
              <img
                src="https://empire-dance-media.sgp1.cdn.digitaloceanspaces.com/trive.png"
                alt="Thrive Wellness Partner"
                className="h-8 md:h-12 w-auto object-contain"
              />
            </div>
            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] md:text-xs font-semibold uppercase tracking-widest text-empire-gold mb-0.5">Wellness Partner</p>
              <h3 className="text-base md:text-2xl font-bold text-white mb-1 leading-tight">Up to 20% Off Annual Package</h3>
              <p className="text-neutral-400 text-xs md:text-sm hidden sm:block">
                Exclusive benefit for Empire Dance Kenya members. Save up to 20% on annual packages.
              </p>
            </div>
            {/* CTA */}
            <div className="flex-shrink-0">
              <Link to="/packages">
                <Button className="btn-empire rounded-full px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm font-semibold whitespace-nowrap">View Packages</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
